import React from 'react';
import { IoLinkOutline, IoPencilOutline, IoChevronUpOutline, IoChevronDownOutline } from 'react-icons/io5';
import { motion } from 'framer-motion';
import { CareerCard, SchemaField, SchemaFieldConfig } from '../types/careerCard';
import { LinkedContentPreview } from './LinkedContentPreview';
import { LinkedContent } from './LinkedContentPreview';
import { useDarkMode } from '../context/DarkModeContext';
import { formatDateOnly } from '../utils/dateUtils';
import { useCachedData } from '../hooks/useCachedData';
import { CACHE_KEYS, CACHE_DURATIONS } from '../constants/cacheKeys';
import { supabase } from '../utils/supabase';
import { useActiveCareerFocus } from '../hooks/useCareerFocus';
import { useGoals } from '../hooks/useGoals';
import { useRubric } from '../hooks/useRubric';
import { useContentLinks } from '../hooks/useContentLinks';
import { useEffect, useState } from 'react';
import { CheckIcon } from '@heroicons/react/24/outline';
import { FocusHistoryDisplay } from './Evidence/FocusHistoryDisplay';
import GoalSelector from './CareerFocus/GoalSelector';
import RubricCriteriaSelector from './CareerFocus/RubricCriteriaSelector';

interface ViewModeProps {
  card: CareerCard;
  schema: Record<string, SchemaField> | undefined;
  linkedContent: LinkedContent | null;
  onLinkWorkExperience: (workExpId: string) => void;
  onUnlinkWorkExperience: () => void;
  isSchemaLoading?: boolean;
}

interface WorkExperience {
  id: string;
  company: string;
  position: string;
  start_date: string;
  end_date: string | null;
}

const EmptyField: React.FC<{ message?: string }> = ({ message = "No data entered yet" }) => (
  <div className="text-gray-400 dark:text-gray-500 italic text-sm">
    {message}
  </div>
);

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h3 className="text-lg font-semibold mb-2">
    {children}
  </h3>
);

export const ViewMode: React.FC<ViewModeProps> = ({
  card,
  schema,
  linkedContent,
  onLinkWorkExperience,
  onUnlinkWorkExperience,
  isSchemaLoading = false,
}) => {
  const { darkMode } = useDarkMode();
  const { focus } = useActiveCareerFocus();
  const { goals } = useGoals(focus?.id || null);
  const { activeRubric } = useRubric(focus?.id);
  const { getFilteredLinks } = useContentLinks();
  const [linkedGoalIds, setLinkedGoalIds] = useState<string[]>([]);
  const [linkedRubricCriteria, setLinkedRubricCriteria] = useState<string[]>([]);
  const [isEditingLinks, setIsEditingLinks] = useState(false);
  const [isFocusLinkageExpanded, setIsFocusLinkageExpanded] = useState(false);
  
  // Theme colors
  const cardBgColor = darkMode ? 'bg-gray-800' : 'bg-gray-50';
  const borderColor = darkMode ? 'border-gray-700' : 'border-gray-200';
  
  const { 
    data: workExperiences = [], 
  } = useCachedData<LinkedContent[]>(
    async () => {
      const { data, error } = await supabase
        .from('work_experience')
        .select('*')
        .order('start_date', { ascending: false });

      if (error) throw error;

      return data.map((exp: WorkExperience) => ({
        id: exp.id,
        type: 'work_experience' as const,
        preview: {
          company: exp.company,
          position: exp.position,
          date_range: {
            start: exp.start_date,
            end: exp.end_date || undefined,
          },
        },
      }));
    },
    {
      cacheKey: CACHE_KEYS.WORK_EXPERIENCES,
      maxAge: CACHE_DURATIONS.SHORT,
      initialData: [],
    }
  );

  // Load linked goals and rubrics
  useEffect(() => {
    const loadLinks = async () => {
      if (!card.cardid || !focus) return;

      try {
        // Load goal links
        const goalLinks = await getFilteredLinks({
          sourceType: 'career_card',
          sourceId: card.cardid,
          targetType: 'goal'
        });
        
        const goalIds = goalLinks.map(link => link.target_id);
        setLinkedGoalIds(goalIds);

        // Load rubric criterion links
        if (activeRubric) {
          const criterionLinks = await getFilteredLinks({
            sourceType: 'career_card',
            sourceId: card.cardid,
            targetType: 'rubric_criterion'
          });
          
          // Extract criterion IDs
          const criteriaIds = criterionLinks.map(link => link.target_id);
          setLinkedRubricCriteria(criteriaIds);
        }
      } catch (error) {
        console.error('Failed to load links:', error);
        setLinkedRubricCriteria([]);
      }
    };

    loadLinks();
  }, [card.cardid, focus, activeRubric, getFilteredLinks]);

  // Turn off edit mode when component unmounts
  useEffect(() => {
    return () => {
      setIsEditingLinks(false);
    };
  }, []);

  // Add this debug function at the top of the ViewMode component
  const debugObject = (obj: any): string => {
    try {
      return JSON.stringify(obj);
    } catch (e) {
      return `[Cannot stringify: ${typeof obj}]`;
    }
  };

  // Add this helper at the top of the component
  const ensureString = (value: unknown): string => {
    if (value === null || value === undefined) {
      return '';
    }
    
    if (typeof value === 'object') {
      try {
        return JSON.stringify(value);
      } catch (e) {
        return '[Complex Object]';
      }
    }
    
    return String(value);
  };

  const renderFieldValue = (field: string, config: SchemaField) => {
    const value = card.card_data[field];
    
    // Enhanced debugging
    if (field === 'achievements') {
      // console.log(`Rendering achievements field:`, {
      //   isArray: Array.isArray(value),
      //   value: debugObject(value),
      //   type: typeof value,
      //   config: debugObject(config)
      // });
      
      // Special case for the achievements field regardless of schema type
      // The achievements field might have schema type 'array' in DB but actual data is complex objects
      if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object') {
        return (
          <div>
            <SectionTitle>{config.label}</SectionTitle>
            <div className="space-y-4">
              {value.map((achievement, index) => {
                // Ensure achievement is an object with expected properties
                if (!achievement || typeof achievement !== 'object') {
                  return <div key={index}>Invalid achievement data</div>;
                }
                
                // Extract safe primitive values
                const safeId = typeof achievement.id === 'string' ? achievement.id : String(index);
                const safeTitle = typeof achievement.title === 'string' ? achievement.title : 
                                (achievement.title ? String(achievement.title) : 'No title');
                const safeDescription = typeof achievement.description === 'string' ? achievement.description : '';
                const safeDate = typeof achievement.date === 'string' ? achievement.date : '';
                const safeImpact = typeof achievement.impact === 'string' ? achievement.impact : '';
                
                return (
                  <div key={safeId} className="pl-4 border-l-2 border-gray-200 dark:border-gray-700">
                    <div className="mb-2">
                      <span className="font-semibold text-gray-700 dark:text-gray-300">Title: </span>
                      {safeTitle}
                    </div>
                    
                    {safeDescription && (
                      <div className="mb-2">
                        <span className="font-semibold italic text-gray-700 dark:text-gray-300">Description: </span>
                        {safeDescription}
                      </div>
                    )}
                    
                    {safeDate && (
                      <div className="mb-2">
                        <span className="font-semibold italic text-gray-700 dark:text-gray-300">Date: </span>
                        {(() => {
                          try {
                            return formatDateOnly(safeDate);
                          } catch (e) {
                            return safeDate;
                          }
                        })()}
                      </div>
                    )}
                    
                    {safeImpact && (
                      <div className="mb-2">
                        <span className="font-semibold italic text-gray-700 dark:text-gray-300">Impact: </span>
                        {safeImpact}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      }
    }
    
    // Safety check for direct object values that aren't arrays or nested types
    if (value !== null && typeof value === 'object' && !Array.isArray(value) && 
        config.type !== 'nested' && config.type !== 'array') {
      console.warn(`Unexpected object value for field ${field} with type ${config.type}`, value);
      return (
        <div>
          <SectionTitle>{config.label}</SectionTitle>
          <p className="text-red-500">[Complex object data cannot be displayed directly]</p>
        </div>
      );
    }
    
    switch (config.type) {
      case 'text':
      case 'textarea':
        return (
          <div>
            <SectionTitle>{config.label}</SectionTitle>
            {value ? (
              <p className="whitespace-pre-wrap">{ensureString(value)}</p>
            ) : (
              <EmptyField />
            )}
          </div>
        );

      case 'hidden':
        // Hidden fields should not be displayed in the UI
        return null;

      case 'nested':
        if (!Array.isArray(value)) {
          return (
            <div>
              <SectionTitle>{config.label}</SectionTitle>
              <EmptyField message="No entries added yet" />
            </div>
          );
        }
        if (value.length === 0) {
          return (
            <div>
              <SectionTitle>{config.label}</SectionTitle>
              <EmptyField message="No entries added yet" />
            </div>
          );
        }
        
        return (
          <div>
            <SectionTitle>{config.label}</SectionTitle>
            <div className="space-y-4">
              {value.map((item: Record<string, unknown>, index: number) => {
                const taskFields = config.fields?.reduce<Record<string, SchemaFieldConfig>>((acc, field) => {
                  if (typeof field === 'object' && 'name' in field) {
                    // Skip hidden fields
                    if (field.type === 'hidden' || field.hidden === true) {
                      return acc;
                    }
                    acc[field.name] = field as SchemaFieldConfig;
                  }
                  return acc;
                }, {});

                return (
                  <div key={item.id?.toString() || index} className="pl-4 border-l-2 border-gray-200 dark:border-gray-700">
                    {Object.entries(taskFields || {}).map(([fieldName, fieldConfig]) => {
                      const fieldValue = item[fieldName];
                      if (fieldValue === null || fieldValue === undefined) return null;
                      
                      const renderValue = () => {
                        try {
                          if (fieldValue === null || fieldValue === undefined) {
                            return '';
                          }
                          
                          if (typeof fieldValue === 'object') {
                            // For date objects or special formatting
                            if (fieldConfig.type === 'date' && typeof fieldValue === 'string') {
                              return formatDateOnly(fieldValue);
                            }
                            
                            // Handle object safely
                            return (
                              <span className="text-gray-600 dark:text-gray-400">
                                {ensureString(fieldValue)}
                              </span>
                            );
                          }
                          
                          // Handle different field types with try/catch for safety
                          switch (fieldConfig.type) {
                            case 'date':
                              try {
                                return formatDateOnly(String(fieldValue));
                              } catch (e) {
                                return String(fieldValue);
                              }
                            case 'textarea':
                            case 'text':
                            case 'select':
                            default:
                              return String(fieldValue);
                          }
                        } catch (e) {
                          console.error(`Error rendering field ${fieldName}:`, e);
                          return '[Error displaying value]';
                        }
                      };
                      
                      return (
                        <div key={fieldName} className="mb-2">
                          <span className="font-semibold italic text-gray-700 dark:text-gray-300">{fieldConfig.label}: </span>
                          {renderValue()}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        );

      case 'array':
        return (
          <div>
            <SectionTitle>{config.label}</SectionTitle>
            {Array.isArray(value) && value.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {value.map((item, index) => {
                  // Handle simple strings/values for typical arrays
                  if (typeof item !== 'object' || item === null) {
                    return (
                      <span
                        key={index}
                        className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-full text-gray-800 dark:text-gray-200"
                      >
                        {ensureString(item)}
                      </span>
                    );
                  }
                  
                  // Special handling for objects in arrays (like achievements)
                  const objId = typeof item.id === 'string' ? item.id : String(index);
                  const objTitle = typeof item.title === 'string' ? item.title : 
                                   (item.title ? String(item.title) : 'Untitled');
                  
                  return (
                    <span
                      key={objId}
                      className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-full text-gray-800 dark:text-gray-200"
                    >
                      {objTitle}
                    </span>
                  );
                })}
              </div>
            ) : (
              <EmptyField message="No items added yet" />
            )}
          </div>
        );

      case 'date':
        return (
          <div>
            <SectionTitle>{config.label}</SectionTitle>
            {value ? (
              <p>
                {(() => {
                  try {
                    return formatDateOnly(ensureString(value));
                  } catch (e) {
                    return ensureString(value);
                  }
                })()}
              </p>
            ) : (
              <EmptyField message="No date set" />
            )}
          </div>
        );

      case 'select':
        return (
          <div>
            <SectionTitle>{config.label}</SectionTitle>
            {value ? (
              <p>{ensureString(value)}</p>
            ) : (
              <EmptyField message="No option selected" />
            )}
          </div>
        );

      default:
        return null;
    }
  };

  if (isSchemaLoading) {
    return (
      <div className="p-4">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-5/6"></div>
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-2/3"></div>
        </div>
      </div>
    );
  }

  if (!schema) {
    return (
      <div className="p-4">
        <div className="text-center text-gray-500 dark:text-gray-400">
          <p>Schema not found for card type: {card.card_data.type}</p>
          <p className="mt-2 text-sm">Please try refreshing the page.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="p-4">
        <div className="prose dark:prose-invert max-w-none space-y-6">
          {Object.entries(schema || {})
            .sort(([, a], [, b]) => (a.order || 0) - (b.order || 0))
            .filter(([, config]) => config.type !== 'hidden' && config.hidden !== true)
            .map(([field, config]) => {
              // Final safety check before rendering
              try {
                return (
                  <React.Fragment key={`${field}-${card.cardid}`}>
                    {renderFieldValue(field, config)}
                  </React.Fragment>
                );
              } catch (error) {
                console.error(`Error rendering field ${field}:`, error);
                return (
                  <div key={`${field}-error-${card.cardid}`}>
                    <SectionTitle>{config.label || field}</SectionTitle>
                    <p className="text-red-500">[Error rendering field]</p>
                  </div>
                );
              }
            })}
        </div>
      </div>
      {/* Links Section */}
      <div className="p-4 border-t border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-medium flex items-center gap-2">
            <IoLinkOutline />
            Linked Content
          </h3>
          {focus && card.cardid && (
            <button
              onClick={() => setIsEditingLinks(!isEditingLinks)}
              className={`
                p-1.5 rounded-lg transition-colors text-sm flex items-center gap-1
                ${isEditingLinks 
                  ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300'
                  : darkMode 
                    ? 'hover:bg-gray-700 text-gray-400 hover:text-gray-200' 
                    : 'hover:bg-gray-100 text-gray-600 hover:text-gray-800'
                }
              `}
              title={isEditingLinks ? "Done editing" : "Edit linked content"}
            >
              <IoPencilOutline className="w-4 h-4" />
              {isEditingLinks ? 'Done' : 'Edit'}
            </button>
          )}
        </div>

        {/* Work Experience Link */}
        <div className="space-y-2">
          <h4 className="text-xs text-gray-500 dark:text-gray-400">Work Experience</h4>
          {isEditingLinks ? (
            // Edit mode - show selector or unlink option
            (linkedContent ? (<LinkedContentPreview
              content={linkedContent}
              onUnlink={onUnlinkWorkExperience}
            />) : (<div className="relative">
              <select
                onChange={(e) => onLinkWorkExperience(e.target.value)}
                value=""
                className={`
                  w-full p-2 rounded border text-sm
                  ${darkMode 
                    ? 'bg-gray-700 border-gray-600 text-white' 
                    : 'bg-white border-gray-300 text-black'
                  }
                `}
                aria-label="Select work experience to link"
              >
                <option value="">Select work experience...</option>
                {workExperiences?.map((exp) => (
                  <option key={exp.id} value={exp.id}>
                    {exp.preview.position} at {exp.preview.company}
                  </option>
                ))}
              </select>
            </div>))
          ) : (
            // View mode - show linked work experience or prompt
            (linkedContent ? (<LinkedContentPreview
              content={linkedContent}
              onUnlink={undefined} // No unlink in view mode
            />) : (<div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                No work experience linked. Click Edit to add a link.
              </p>
            </div>))
          )}
        </div>
        
        {/* Active Focus Links */}
        {focus && card.cardid && (
          <div className="mt-4">
            {isEditingLinks ? (
              // Edit mode - show selectors
              (<div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
                      Link to {focus.company} - {focus.role}
                    </p>
                  </div>
                  <button
                    onClick={() => setIsFocusLinkageExpanded(!isFocusLinkageExpanded)}
                    className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full ml-2"
                    title={isFocusLinkageExpanded ? "Collapse focus linking" : "Expand focus linking"}
                  >
                    {isFocusLinkageExpanded ? <IoChevronUpOutline size={16} /> : <IoChevronDownOutline size={16} />}
                  </button>
                </div>
                
                <motion.div
                  initial={false}
                  animate={{ height: isFocusLinkageExpanded ? "auto" : 0 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  <div className="space-y-4 pt-2">
                    <GoalSelector 
                      cardId={card.cardid}
                      onGoalsUpdated={(goals) => {
                        setLinkedGoalIds(goals);
                      }}
                    />
                    {activeRubric && (
                      <RubricCriteriaSelector
                        cardId={card.cardid}
                        onCriteriaUpdated={(criteria) => {
                          setLinkedRubricCriteria(criteria);
                        }}
                      />
                    )}
                  </div>
                </motion.div>
              </div>)
            ) : (
              // View mode - show under single focus section
              (<>
                {(linkedGoalIds.length > 0 || linkedRubricCriteria.length > 0) ? (
                  <div>
                    <h4 className="text-xs text-gray-500 dark:text-gray-400 mb-2">Active Internship</h4>
                    <div className={`${cardBgColor} ${borderColor} border rounded-lg p-3`}>
                      <div className="space-y-3">
                        {/* Focus Info */}
                        <div className="font-medium text-sm">
                          {focus.company} - {focus.role}
                        </div>
                        
                        {/* Linked Goals */}
                        {linkedGoalIds.length > 0 && goals && (
                          <div>
                            <h5 className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Linked Goals:</h5>
                            <div className="space-y-1 ml-2">
                              {linkedGoalIds.map(goalId => {
                                const goal = goals.find(g => g.id === goalId);
                                if (!goal) return null;
                                return (
                                  <div key={goalId} className="flex items-center gap-2 text-sm">
                                    <CheckIcon className="w-3 h-3 text-green-500 shrink-0" />
                                    <span className="text-gray-700 dark:text-gray-300">
                                      {goal.goal_text}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        
                        {/* Linked Rubric Criteria */}
                        {linkedRubricCriteria.length > 0 && activeRubric && (
                          <div>
                            <h5 className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Linked Evaluation Criteria:</h5>
                            <div className="space-y-2 ml-2">
                              {activeRubric.categories.map(category => {
                                // Find criteria in this category that are linked
                                const linkedInCategory = linkedRubricCriteria.filter(criterionId => {
                                  // Check if this criterion belongs to this category
                                  if (category.criteria_items) {
                                    return category.criteria_items.some(item => item.id === criterionId);
                                  }
                                  return false;
                                });
                                
                                if (linkedInCategory.length === 0) return null;
                                
                                return (
                                  <div key={category.id}>
                                    <h6 className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">{category.name}</h6>
                                    <div className="space-y-1 mt-1">
                                      {category.criteria_items?.map(criterion => {
                                        if (!linkedRubricCriteria.includes(criterion.id)) return null;
                                        return (
                                          <div key={criterion.id} className="flex items-start gap-2 text-sm">
                                            <CheckIcon className="w-3 h-3 text-blue-500 shrink-0 mt-0.5" />
                                            <span className="text-gray-700 dark:text-gray-300">
                                              {criterion.text}
                                            </span>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  // No links message
                  (<div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {isEditingLinks 
                        ? "Select goals and rubric categories to link this card to your focus areas."
                        : "No goals or rubric categories linked. Click Edit to add links."
                      }
                    </p>
                  </div>)
                )}
              </>)
            )}
          </div>
        )}
        
        {/* Show message for unsaved cards */}
        {focus && !card.cardid && (
          <div className="mt-4">
            <h4 className="text-xs text-gray-500 dark:text-gray-400 mb-2">Evidence Linking</h4>
            <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                Save the card to link it to goals and rubric categories
              </p>
            </div>
          </div>
        )}
        
        {/* Focus History Display - shows all linked focuses including inactive ones */}
        {card.cardid && (
          <FocusHistoryDisplay 
            itemType="career_card" 
            itemId={card.cardid} 
            className="mt-4"
          />
        )}
      </div>
    </>
  );
}; 